import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { COLLECTIONS } from "@/pages/admin/schemas";
import { ImageUploadField, ImageListField } from "@/components/admin/ImageUploadField";

const inputCls = "w-full rounded-lg border border-white/10 bg-neutral-900 px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";
const SEP = " :: ";

const toText = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromText = (t) => (t || "").split("\n").map((l) => l.trim()).filter(Boolean);
const stepsToText = (arr) => (Array.isArray(arr) ? arr.map((s) => `${s.title || ""}${SEP}${s.desc || ""}`).join("\n") : "");
const textToSteps = (t) => (t || "").split("\n").filter((l) => l.trim()).map((l) => { const [title, ...rest] = l.split(SEP); return { title: (title || "").trim(), desc: rest.join(SEP).trim() }; });
const faqsToText = (arr) => (Array.isArray(arr) ? arr.map((f) => `${f.question || ""}${SEP}${f.answer || ""}`).join("\n") : "");
const textToFaqs = (t) => (t || "").split("\n").filter((l) => l.trim()).map((l) => { const [q, ...rest] = l.split(SEP); return { question: (q || "").trim(), answer: rest.join(SEP).trim() }; });

function Field({ field, value, onChange }) {
  const { type, label, full, preset } = field;
  const cls = full ? "sm:col-span-2" : "";

  if (type === "switch") {
    return (
      <label className={`flex items-center gap-3 ${cls}`}>
        <button type="button" onClick={() => onChange(!value)} className={`relative h-6 w-11 rounded-full transition-colors ${value ? "bg-gold" : "bg-neutral-700"}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
        <span className="text-sm font-medium text-neutral-300">{label}</span>
      </label>
    );
  }

  if (type === "image") {
    return <ImageUploadField value={value} onChange={onChange} preset={preset || "hero"} label={label} testid={`img-${field.name}`} />;
  }
  if (type === "imagelist") {
    return <ImageListField value={value} onChange={onChange} preset={preset || "gallery"} label={label} testid={`imglist-${field.name}`} />;
  }

  return (
    <div className={cls}>
      <label className="mb-1.5 block text-xs font-medium text-neutral-400">{label}</label>
      {type === "textarea" && <textarea rows={3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />}
      {type === "longtext" && <textarea rows={7} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />}
      {type === "number" && <input type="number" className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />}
      {type === "stringlist" && <textarea rows={4} className={inputCls} value={toText(value)} onChange={(e) => onChange(fromText(e.target.value))} placeholder="Her satır bir madde" />}
      {type === "steplist" && (
        <>
          <textarea rows={5} className={inputCls} value={stepsToText(value)} onChange={(e) => onChange(textToSteps(e.target.value))} placeholder={`Başlık${SEP}Açıklama (her satır bir adım)`} />
          <p className="mt-1 text-[11px] text-neutral-600">Format: Başlık{SEP}Açıklama</p>
        </>
      )}
      {type === "faqlist" && (
        <>
          <textarea rows={5} className={inputCls} value={faqsToText(value)} onChange={(e) => onChange(textToFaqs(e.target.value))} placeholder={`Soru${SEP}Cevap (her satır bir SSS)`} />
          <p className="mt-1 text-[11px] text-neutral-600">Format: Soru{SEP}Cevap</p>
        </>
      )}
      {(type === "text" || !type) && <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

export default function CollectionAdmin() {
  const { collection } = useParams();
  const config = COLLECTIONS[collection];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);

  async function load() {
    if (!config) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${config.endpoint}`);
      setItems(data);
    } catch {
      toast.error("Liste yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  if (!config) return <div className="text-neutral-400">Bilinmeyen bölüm.</div>;

  function startCreate() {
    const blank = {};
    config.fields.forEach((f) => {
      blank[f.name] = f.type === "switch" ? true : f.type === "stringlist" || f.type === "steplist" || f.type === "faqlist" ? [] : "";
    });
    setEditing(blank);
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...editing };
      if (payload.id) {
        const id = payload.id;
        delete payload.id;
        await api.put(`/admin/${config.endpoint}/${id}`, payload);
      } else {
        await api.post(`/admin/${config.endpoint}`, payload);
      }
      toast.success("Kaydedildi.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/admin/${config.endpoint}/${id}`);
      toast.success("Silindi.");
      load();
    } catch {
      toast.error("Silinemedi.");
    }
  }

  return (
    <div data-testid={`admin-${collection}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium text-white">{config.label}</h1>
          <p className="mt-1 text-sm text-neutral-500">{items.length} kayıt</p>
        </div>
        <button onClick={startCreate} data-testid="admin-create-btn" className="inline-flex items-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-sm font-bold text-black hover:bg-gold-300 transition-colors">
          <Plus className="h-4 w-4" /> Yeni Ekle
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-neutral-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-neutral-500">Henüz kayıt yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-5 py-3">Başlık</th>
                <th className="px-5 py-3">Durum</th>
                <th className="px-5 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-white/[0.02]">
                  <td className="px-5 py-3.5 font-medium text-white">{it[config.titleField] || it.slug || it.id}</td>
                  <td className="px-5 py-3.5">
                    {"published" in it || "active" in it ? (
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${it.published || it.active ? "bg-green-500/15 text-green-400" : "bg-neutral-700/40 text-neutral-400"}`}>
                        {it.published || it.active ? "Yayında" : "Pasif"}
                      </span>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setEditing({ ...it })} data-testid={`admin-edit-${it.id}`} className="rounded-lg border border-white/10 p-2 text-neutral-300 hover:border-gold/40 hover:text-gold transition-colors"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(it.id)} data-testid={`admin-delete-${it.id}`} className="rounded-lg border border-white/10 p-2 text-neutral-300 hover:border-red-500/40 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={() => setEditing(null)}>
          <div className="my-8 w-full max-w-3xl rounded-2xl border border-white/10 bg-neutral-950 p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-edit-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-medium text-white">{editing.id ? "Düzenle" : "Yeni Kayıt"}</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-2 text-neutral-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {config.fields.map((f) => (
                <Field key={f.name} field={f} value={editing[f.name]} onChange={(v) => setEditing((e) => ({ ...e, [f.name]: v }))} />
              ))}
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-xl border border-white/15 px-5 py-2.5 text-sm font-semibold text-white hover:border-white/30 transition-colors">İptal</button>
              <button onClick={save} disabled={saving} data-testid="admin-save-btn" className="inline-flex items-center gap-2 rounded-xl bg-gold px-6 py-2.5 text-sm font-bold text-black hover:bg-gold-300 transition-colors disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
